import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import useSnackbarStore from '@store/snackbar-store/snackbar.store.ts';
import { SnackbarType } from '@models/app-snackbar.model.ts';
import { CallStateEnum } from '@models/call-state.model.ts';
import { withCallState } from '@shared/store-features/call-state-feature.ts';
import axios from 'axios';
import {
  DEPARTMENT_INITIAL_STATE,
  DepartmentActionParams,
  DepartmentState
} from '@modules/departments-and-units/pages/departments/store/departments.model.ts';
import { DepartmentReqModel } from '@models/departments-and-units/departments.model.ts';
import {
  createDepartment,
  editDepartment,
  changeStatus
} from '@api/departments-and-units/departments.api.ts';
import { getDepartments } from '@api/hotel-staff/hotel-staff-api.ts';

const { showSnackbar } = useSnackbarStore.getState();

const useDepartmentStore = create<DepartmentState>()(
  devtools(
    (set, getState) => ({
      ...DEPARTMENT_INITIAL_STATE,
      ...withCallState(getState),

      getDepartmentsList: async () => {
        try {
          const departmentsData = await getDepartments();

          const departmentsList = departmentsData.map((dep) => ({
            id: dep.id,
            title: dep.name,
            value: dep.name
          }));

          set({
            departments: departmentsData,
            departmentsList
          });
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      createDepartment: async ({
        data,
        state,
        handleClose
      }: DepartmentActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          const createdDepartment = await createDepartment(
            data as DepartmentReqModel
          );
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          if (state) state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(
            'snackbar_success_department_text',
            SnackbarType.Success
          );
          return createdDepartment;
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
          throw error;
        }
      },

      editDepartment: async ({
        data,
        id,
        state,
        handleClose
      }: DepartmentActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await editDepartment(data as DepartmentReqModel, id as number);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_update_department_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      changeStatus: async ({
        id,
        isActive,
        state,
        handleClose
      }: DepartmentActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          const data = await changeStatus(id as number, isActive as boolean);

          const snackbarText = data?.isActive
            ? 'snackbar_activate_department_text'
            : 'snackbar_deactivate_department_text';

          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(snackbarText, SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      }
    }),
    { name: 'DepartmentStore' }
  )
);

export default useDepartmentStore;
